import i18n from 'i18n';
import { DeviceUnitSortOrder } from 'core/deviceManagement/DeviceManagement';
import { DeviceManagementFilter } from './deviceFilters';
import { AvailabilityColumn } from './availabilityColumns';
import {
  DEFAULT_DEVICE_SORT,
  DeviceListColumn,
  LIST_SORT_FIELDS
} from './deviceListColumns';

/** 匯出的範圍。 */
export enum DeviceExportScope {
  ALL = 'all',
  TOP_N = 'topN'
}

export enum DeviceExportError {
  /** 選了「前 X 筆」但 X 不是正整數。 */
  INVALID_COUNT = 'invalidCount',
  /** 選中的範圍不在選項清單內。 */
  UNKNOWN_SCOPE = 'unknownScope'
}

export type DeviceExportResolution =
  | { ok: true; limit?: number }
  | { ok: false; reason: DeviceExportError };

export const DEVICE_EXPORT_ERROR_I18N: Record<DeviceExportError, string> = {
  [DeviceExportError.INVALID_COUNT]:
    'deviceManagement.messages.exportInvalidCount',
  [DeviceExportError.UNKNOWN_SCOPE]:
    'deviceManagement.messages.exportUnknownScope'
};

/**
 * 把畫面上的選擇換成匯出筆數上限（不給上限就是全部）。
 *
 * 每一條路徑都有明確結果，包含「認不得的範圍」那一條。少了最後那個 return，
 * 選中值不在清單內時按下下載會靜默什麼都不做 —— 使用者只會覺得按鈕壞了，
 * 畫面上沒有任何線索。匯出報表的面板踩過這個形狀，不要複製它。
 */
export function resolveExportLimit(
  scope: string,
  countInput: string
): DeviceExportResolution {
  if (scope === DeviceExportScope.ALL) {
    return { ok: true };
  }
  if (scope === DeviceExportScope.TOP_N) {
    const count = Number(countInput);
    if (!Number.isInteger(count) || count <= 0 || countInput.trim() === '') {
      return { ok: false, reason: DeviceExportError.INVALID_COUNT };
    }
    return { ok: true, limit: count };
  }
  return { ok: false, reason: DeviceExportError.UNKNOWN_SCOPE };
}

/**
 * 匯出端點排得動的欄位，就是兩個分頁的欄位定義本身。
 *
 * 從欄位定義推導，不另寫一份會腐化的清單：某一頁加了可排序的欄位而這裡沒跟上，
 * 那一欄一排序就匯出不了。
 *
 * 可售率那四欄以前刻意不在裡面 —— 產檔那條路徑沒有算可售秒數，送過去會被後端擋掉。
 * 現在匯出端點收得下走期與投放時段，算得出那幾個值，所以它們與清單那幾欄同級。
 */
export const EXPORTABLE_SORT_FIELDS: string[] = [
  ...LIST_SORT_FIELDS,
  ...Object.values(AvailabilityColumn)
];

/**
 * 這次匯出真正會照的順序。
 *
 * 認不得的欄位退回預設排序，檔案照樣拿得到 —— 這道防線留著是為了「畫面上多了一個
 * 後端還不認得的可排序欄位」那種情形，不是為了可售率那四欄。面板上那句排序摘要走
 * 同一支，否則面板會說一個順序而檔案是另一個。
 */
export function resolveExportSort(
  sortField: string,
  sortOrder: DeviceUnitSortOrder
): { sortField: string; sortOrder: DeviceUnitSortOrder } {
  return EXPORTABLE_SORT_FIELDS.includes(sortField)
    ? { sortField, sortOrder }
    : DEFAULT_DEVICE_SORT;
}

/**
 * 可排序的欄位 → 匯出面板上寫出來的名字。
 *
 * 用一份明寫的對照表，不用 `deviceManagement.headers.<欄位名>` 組出 key：
 * 少一支 key 時 i18n 會把 key 本身印在畫面上，而那長得就像正常文字，沒有人會發現。
 * 新增可排序的欄位時這裡要跟著加，deviceExport.test 會擋住漏掉的那一個。
 */
export const DEVICE_SORT_FIELD_I18N: Record<string, string> = {
  [DeviceListColumn.UNIT_NAME]: 'deviceManagement.headers.unitName',
  [DeviceListColumn.STORE_NUM]: 'deviceManagement.headers.storeNum',
  [DeviceListColumn.DEVICE_CHARACTERISTIC]:
    'deviceManagement.headers.deviceCharacteristic',
  [DeviceListColumn.HEALTH_STATE]: 'deviceManagement.headers.healthState',
  [DeviceListColumn.ADDRESS]: 'deviceManagement.headers.address',
  [DeviceListColumn.LAST_REPORTED_AT]:
    'deviceManagement.headers.lastReportedAt',
  [AvailabilityColumn.DAILY_AVERAGE_FOOTFALL]:
    'deviceManagement.headers.dailyAverageFootfall',
  [AvailabilityColumn.AVAILABILITY_RATE]:
    'deviceManagement.headers.availabilityRate',
  [AvailabilityColumn.AVAILABLE_SECONDS]:
    'deviceManagement.headers.availableSeconds',
  [AvailabilityColumn.SOLD_SECONDS]: 'deviceManagement.headers.soldSeconds'
};

/**
 * 這次匯出會照什麼順序排。
 *
 * 寫的是檔案真正會用的那個排序，不是畫面上當下的那個。兩邊各自判斷的話，面板寫的
 * 會是一個順序而檔案是另一個，而使用者要到打開檔案才發現。
 *
 * 沒動過排序時寫的是「預設排序（狀態異常優先）」而不是「狀態 由低至高」：
 * healthState 在後端是依嚴重度排的，照字面寫成「由低至高」，使用者會以為是字母序。
 * 判斷依據是同一份 DEFAULT_DEVICE_SORT，所以預設值改了這句話會跟著改。
 */
export function buildExportSortLabel(
  sortField: string,
  sortOrder: DeviceUnitSortOrder
): string {
  const sort = resolveExportSort(sortField, sortOrder);
  if (
    sort.sortField === DEFAULT_DEVICE_SORT.sortField &&
    sort.sortOrder === DEFAULT_DEVICE_SORT.sortOrder
  ) {
    return i18n.t('deviceManagement.labels.exportDefaultSort');
  }
  const fieldKey = DEVICE_SORT_FIELD_I18N[sort.sortField];
  const field = fieldKey ? i18n.t(fieldKey) : sort.sortField;
  return i18n.t(
    sort.sortOrder === 'desc'
      ? 'deviceManagement.labels.exportSortDesc'
      : 'deviceManagement.labels.exportSortAsc',
    { field }
  );
}

/**
 * 這次匯出套了哪些條件。
 *
 * 搜尋字串算一項：它與四個下拉一樣會縮小匯出的範圍，漏掉它會讓「未套用篩選」
 * 出現在一份其實被搜尋過的檔案上。名單模式另外寫出來，因為它框住的是母體，
 * 與其他條件不是同一件事。
 */
export function buildExportFilterLabel(
  filter: DeviceManagementFilter,
  search: string,
  listFileName?: string
): string {
  const total =
    Object.values(filter).reduce((sum, values) => sum + values.length, 0) +
    (search.trim() === '' ? 0 : 1);
  const applied =
    total > 0
      ? i18n.t('deviceManagement.labels.exportFilterApplied', { total })
      : i18n.t('deviceManagement.labels.exportNoFilter');
  if (!listFileName) {
    return applied;
  }
  const listMode = i18n.t('deviceManagement.labels.exportListMode', {
    fileName: listFileName
  });
  return `${listMode} · ${applied}`;
}
